import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Button } from 'src/components/ui/button';
import { Switch } from 'src/components/ui/switch';
import { Loader2 } from 'lucide-react';
import { cn } from 'src/lib/utils';

const defaultFormData = {
  code: '',
  display_name: '',
  description: '',
  enabled: true,
};

// `code` is uppercase letters/digits/underscores only, per the assessment-type
// API doc — auto-uppercased as the admin types rather than rejected on submit.
const sanitizeCode = (value) => value.toUpperCase().replace(/[^A-Z0-9_]/g, '');

const FieldLabel = ({ htmlFor, children, required }) => (
  <label
    htmlFor={htmlFor}
    className="block text-[12.5px] font-[560] text-foreground mb-1.5 tracking-[.005em]"
  >
    {children}
    {required && <span className="text-destructive ml-0.5">*</span>}
  </label>
);

FieldLabel.propTypes = {
  htmlFor: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  required: PropTypes.bool,
};

FieldLabel.defaultProps = {
  required: false,
};

const fieldBase =
  'w-full h-9 px-3 py-2 rounded-lg border border-input bg-card text-sm text-foreground-strong placeholder:text-muted-foreground outline-none transition-[border-color,box-shadow] focus:border-primary focus:ring-[3px] focus:ring-primary/[0.08] disabled:opacity-50 disabled:cursor-not-allowed';

// `code` is immutable after creation per the API doc, so it's disabled once
// there's an initialData row to edit — only display_name/description/enabled
// are ever sent on an update.
export const AssessmentTypeModal = ({
  onSubmit,
  onCancel,
  initialData,
  loading,
}) => {
  const [formData, setFormData] = useState({
    ...defaultFormData,
    ...(initialData && {
      code: initialData.code ?? '',
      display_name: initialData.display_name ?? '',
      description: initialData.description ?? '',
      enabled: initialData.enabled ?? true,
    }),
  });

  const isEditMode = Boolean(initialData?.code);
  const buttonText = isEditMode ? 'Save Changes' : 'Create Assessment Type';
  const loadingText = isEditMode ? 'Saving...' : 'Creating...';

  const set = (key) => (e) =>
    setFormData((prev) => ({ ...prev, [key]: e.target.value }));

  const isFormValid =
    formData.code.trim() !== '' &&
    formData.code.length <= 50 &&
    formData.display_name.trim() !== '' &&
    formData.display_name.length <= 100 &&
    formData.description.length <= 200;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!isFormValid) return;
    if (isEditMode) {
      onSubmit({
        display_name: formData.display_name.trim(),
        description: formData.description.trim() || null,
        enabled: formData.enabled,
      });
    } else {
      onSubmit({
        code: formData.code.trim(),
        display_name: formData.display_name.trim(),
        description: formData.description.trim() || null,
        enabled: formData.enabled,
      });
    }
  };

  return (
    <form onSubmit={handleSubmit} className="flex flex-col flex-1 min-h-0">
      <div className="flex-1 overflow-y-auto px-[22px] py-[18px] space-y-4">
        <div>
          <FieldLabel htmlFor="assessment-type-code" required>
            Code
          </FieldLabel>
          <input
            id="assessment-type-code"
            className={fieldBase}
            value={formData.code}
            onChange={(e) =>
              setFormData((prev) => ({
                ...prev,
                code: sanitizeCode(e.target.value),
              }))
            }
            placeholder="e.g. TEAMS_INTERVIEW"
            maxLength={50}
            disabled={loading || isEditMode}
          />
          {isEditMode && (
            <p className="text-[11.5px] text-muted-foreground mt-1">
              Code can&apos;t be changed after creation.
            </p>
          )}
        </div>

        <div>
          <FieldLabel htmlFor="assessment-type-display-name" required>
            Display Name
          </FieldLabel>
          <input
            id="assessment-type-display-name"
            className={fieldBase}
            value={formData.display_name}
            onChange={set('display_name')}
            placeholder="e.g. Teams Interview"
            maxLength={100}
            disabled={loading}
          />
        </div>

        <div>
          <FieldLabel htmlFor="assessment-type-description">
            Description
          </FieldLabel>
          <textarea
            id="assessment-type-description"
            className={cn(fieldBase, 'resize-y min-h-[80px] leading-[1.65]')}
            value={formData.description}
            onChange={set('description')}
            placeholder="Brief description of the assessment type…"
            maxLength={200}
            disabled={loading}
          />
        </div>

        <div className="border-t border-border pt-4 flex items-center gap-3">
          <Switch
            checked={formData.enabled}
            onCheckedChange={(checked) =>
              setFormData((p) => ({ ...p, enabled: checked }))
            }
            disabled={loading}
          />
          <span className="text-[13px] font-medium text-foreground">
            {formData.enabled ? 'Enabled' : 'Disabled'}
          </span>
        </div>
      </div>

      <div className="flex justify-end gap-2 px-[22px] py-[14px] border-t border-border bg-brand-bg-subtle shrink-0">
        {onCancel && (
          <Button
            type="button"
            variant="outline"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </Button>
        )}
        <Button type="submit" disabled={loading || !isFormValid}>
          {loading ? (
            <>
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              {loadingText}
            </>
          ) : (
            buttonText
          )}
        </Button>
      </div>
    </form>
  );
};

AssessmentTypeModal.propTypes = {
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func,
  initialData: PropTypes.shape({
    code: PropTypes.string,
    display_name: PropTypes.string,
    description: PropTypes.string,
    enabled: PropTypes.bool,
  }),
  loading: PropTypes.bool,
};

AssessmentTypeModal.defaultProps = {
  onCancel: undefined,
  initialData: null,
  loading: false,
};

export default AssessmentTypeModal;
